import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { STRINGS } from "./strings";
import * as sfx from "./sfx";
import { SCENE_TYPES, SCENE_ANALYSIS, FILTERS, FILTER_BY_ID, TEMPLATES, TEMPLATE_BY_ID, EXERCISES, AR_GUIDES } from "@/data/cinema";
import { SCENE_TYPES_EN, SCENE_ANALYSIS_EN, FILTERS_EN, FILTER_BY_ID_EN, TEMPLATES_EN, TEMPLATE_BY_ID_EN, EXERCISES_EN, AR_GUIDES_EN } from "./dataEn";

const Ctx = createContext(null);

const readStored = () => { try { return localStorage.getItem("cineart_lang"); } catch (e) { return null; } };

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => readStored() || "ar");
  const [chosen, setChosen] = useState(() => !!readStored());
  const [muted, setMutedState] = useState(sfx.isMuted());
  const [fading, setFading] = useState(false);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  const setLang = useCallback((l, { animate = true } = {}) => {
    try { localStorage.setItem("cineart_lang", l); } catch (e) {}
    setChosen(true);
    if (!animate) { setLangState(l); return; }
    setFading(true);
    setTimeout(() => { setLangState(l); setTimeout(() => setFading(false), 60); }, 320);
  }, []);

  const toggleMute = useCallback(() => {
    setMutedState((m) => { const nv = !m; sfx.setMuted(nv); return nv; });
  }, []);

  const t = useCallback((key) => {
    const s = STRINGS[lang] || STRINGS.ar;
    return s[key] ?? STRINGS.ar[key] ?? key;
  }, [lang]);

  // نقرة ناعمة موحّدة عند كل ضغطة زر داخل التطبيق
  useEffect(() => {
    const h = (e) => {
      const el = e.target;
      if (el && el.closest && el.closest('button, [role="button"], a')) sfx.playClick();
    };
    document.addEventListener("click", h, true);
    return () => document.removeEventListener("click", h, true);
  }, []);

  const value = useMemo(() => ({
    lang, chosen, setLang, t, dir: lang === "ar" ? "rtl" : "ltr",
    muted, toggleMute, sfx,
  }), [lang, chosen, setLang, t, muted, toggleMute]);

  return (
    <Ctx.Provider value={value}>
      {children}
      <div
        className="fixed inset-0 z-[70] pointer-events-none"
        style={{ background: "#04060d", opacity: fading ? 1 : 0, transition: "opacity 0.32s ease" }}
      />
    </Ctx.Provider>
  );
}

export const useLang = () => useContext(Ctx);

// بيانات المحتوى حسب اللغة (نفس البنية)
export function useCinema() {
  const { lang } = useLang();
  return useMemo(() => (lang === "en"
    ? { SCENE_TYPES: SCENE_TYPES_EN, SCENE_ANALYSIS: SCENE_ANALYSIS_EN, FILTERS: FILTERS_EN, FILTER_BY_ID: FILTER_BY_ID_EN, TEMPLATES: TEMPLATES_EN, TEMPLATE_BY_ID: TEMPLATE_BY_ID_EN, EXERCISES: EXERCISES_EN, AR_GUIDES: AR_GUIDES_EN }
    : { SCENE_TYPES, SCENE_ANALYSIS, FILTERS, FILTER_BY_ID, TEMPLATES, TEMPLATE_BY_ID, EXERCISES, AR_GUIDES }
  ), [lang]);
}
